import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "../../lib/auth.jsx";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Storefront, Camera, ListBullets, ShoppingBag, Eye,
  CheckCircle, X, Warning, FloppyDisk, UploadSimple, Trash, CaretDown,
  Globe, EyeSlash, LinkSimple,
} from "@phosphor-icons/react";
import { ProfileBody } from "./TruckProfilePreview";
import { PUBLIC_WEB_ORIGIN } from "../../lib/backendBase";
import LogoFocalCropper from "../../components/LogoFocalCropper";

/**
 * ProfileEditorPreview — admin-only step-based editor for truck-profile drafts.
 *
 * Hidden from production: route only mounted when `user.is_admin === true`,
 * backend endpoints all gated by `require_admin`, and saving never publishes.
 *
 * Step flow:
 *   1) Basic info
 *   2) Photos (logo + cover + gallery)
 *   3) Links & menu
 *   4) Online ordering
 *   5) Preview
 */

const STEPS = [
  { key: "basic", label: "Basic Info", icon: Storefront },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "links", label: "Links & Menu", icon: ListBullets },
  { key: "ordering", label: "Online Ordering", icon: ShoppingBag },
  { key: "preview", label: "Preview", icon: Eye },
];

const ORDERING_LABELS = ["Order Online", "Preorder", "Order Ahead"];
const ORDERING_AVAILABILITY = [
  { v: "always", label: "Always show" },
  { v: "open_only", label: "Only when truck is open" },
  { v: "events_only", label: "Only during selected events" },
];

export default function ProfileEditorPreview({ mode = "admin" } = {}) {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const truckId = searchParams.get("truck") || "";
  const [trucks, setTrucks] = useState([]);
  const [trucksLoading, setTrucksLoading] = useState(true);
  const [trucksError, setTrucksError] = useState("");
  const [draft, setDraft] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);

  // URL prefix swaps the entire editor between admin-on-anyone and
  // owner-on-mine API paths. Same component, two distinct gates.
  const apiPrefix = mode === "owner" ? "/owner" : "/admin";
  // Auth gate: in admin mode, must be admin. In owner mode, must be a
  // logged-in owner OR admin (admins can also use the owner editor).
  const allowed =
    mode === "owner"
      ? !!user && (user.role === "owner" || user.is_admin)
      : !!user?.is_admin;

  // Welcome toast for newly-paid Pros — consumes the one-shot flag
  // set by `/billing/success` when the Stripe webhook flipped
  // `is_pro=true`. Fires exactly once per session so the toast
  // doesn't re-appear if the owner reopens the editor later.
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem("tt_just_upgraded_pro");
      if (flag === "1") {
        sessionStorage.removeItem("tt_just_upgraded_pro");
        toast.success("You're on Pro — welcome!", {
          description: "Upload your logo, cover photo, and menu. Publish when you're ready.",
          duration: 6000,
        });
      }
    } catch {
      /* private-mode Safari can throw on sessionStorage — non-fatal */
    }
  }, []);

  // 1. Load truck list
  useEffect(() => {
    if (!allowed) return;
    setTrucksLoading(true);
    setTrucksError("");
    api
      .get(`${apiPrefix}/profile-drafts`)
      .then(({ data }) => setTrucks(data?.trucks || []))
      .catch((e) => {
        const code = e?.response?.status;
        if (code === 404) setTrucksError("This feature isn't on production yet — redeploy preview to ship it.");
        else if (code === 403) setTrucksError(mode === "owner" ? "Owner accounts only. Sign in as a truck owner." : "Admins only. Sign in as an admin.");
        else if (code === 401) setTrucksError("Sign in to continue.");
        else setTrucksError("Couldn't load trucks. Try again in a moment.");
      })
      .finally(() => setTrucksLoading(false));
  }, [user, allowed, apiPrefix, mode]);

  // 2. Load the chosen draft
  useEffect(() => {
    if (!truckId || !allowed) {
      setDraft(null);
      return;
    }
    setLoading(true);
    api
      .get(`${apiPrefix}/profile-drafts/${encodeURIComponent(truckId)}`)
      .then(({ data }) => setDraft(data))
      .catch(() => toast.error("Couldn't load draft"))
      .finally(() => setLoading(false));
  }, [truckId, allowed, apiPrefix]);

  // Reload preview composition when entering step 5
  useEffect(() => {
    if (STEPS[step].key !== "preview" || !truckId) return;
    api.get(`${apiPrefix}/truck-profile/${encodeURIComponent(truckId)}`).then(({ data }) => setPreviewData(data)).catch(() => {});
  }, [step, truckId, apiPrefix]);

  if (authLoading) return null;
  if (!allowed) {
    if (mode === "owner") {
      return (
        <div className="max-w-md mx-auto p-8 text-center">
          <Warning weight="duotone" className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-display font-bold text-xl mb-2">Pro feature</h1>
          <p className="text-sm text-muted-foreground">
            Building a public profile is a Pro perk. Upgrade to publish a
            shareable <code className="font-mono text-xs">/t/&#123;your-slug&#125;</code> page customers can find on the web.
          </p>
          <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">← Back to dashboard</Link>
        </div>
      );
    }
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <Warning weight="duotone" className="h-10 w-10 text-rose-500 mx-auto mb-3" />
        <h1 className="font-display font-bold text-xl mb-2">Admins only</h1>
        <p className="text-sm text-muted-foreground">This editor is gated to admins until launch.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-primary hover:underline">← Back to map</Link>
      </div>
    );
  }

  const onSelectTruck = (id) => setSearchParams({ truck: id });

  const onSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await api.put(`${apiPrefix}/profile-drafts/${encodeURIComponent(draft.truck_id)}`, draft);
      setDraft(data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const updateOrdering = (patch) => setDraft((d) => ({ ...d, ordering: { ...d.ordering, ...patch } }));

  const onPublish = async () => {
    if (!draft) return;
    if (!window.confirm("This makes the profile visible to all customers. Continue?")) return;
    try {
      const { data } = await api.post(`${apiPrefix}/profile-drafts/${encodeURIComponent(draft.truck_id)}/publish`);
      setDraft((d) => ({ ...d, is_published: true, truck_slug: data.truck_slug }));
      toast.success(`Published! Live at /t/${data.truck_slug}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Publish failed";
      toast.error(msg);
    }
  };

  const onUnpublish = async () => {
    if (!draft) return;
    if (!window.confirm("Hide this profile from customers? You can republish anytime.")) return;
    try {
      await api.post(`${apiPrefix}/profile-drafts/${encodeURIComponent(draft.truck_id)}/unpublish`);
      setDraft((d) => ({ ...d, is_published: false }));
      toast.success("Profile unpublished");
    } catch (e) {
      toast.error("Unpublish failed");
    }
  };

  return (
    <div className="min-h-dvh bg-background pb-20">
      <Header mode={mode} />

      {!truckId ? (
        <TruckPicker
          trucks={trucks}
          onSelect={onSelectTruck}
          loading={trucksLoading}
          error={trucksError}
        />
      ) : loading || !draft ? (
        <div className="p-8 text-center text-muted-foreground">Loading draft…</div>
      ) : (
        <div className="max-w-2xl mx-auto px-4 mt-4">
          <ProgressBar pct={draft.completion_pct || 0} />
          <Stepper step={step} onStep={setStep} />

          {STEPS[step].key === "basic" && <StepBasic draft={draft} update={update} />}
          {STEPS[step].key === "photos" && <StepPhotos draft={draft} update={update} apiPrefix={apiPrefix} isPro={!!(user?.is_pro || user?.is_admin)} />}
          {STEPS[step].key === "links" && <StepLinks draft={draft} update={update} apiPrefix={apiPrefix} />}
          {STEPS[step].key === "ordering" && <StepOrdering draft={draft} updateOrdering={updateOrdering} />}
          {STEPS[step].key === "preview" && (
            previewData ? (
              <div className="mt-4 -mx-4 border border-border rounded-2xl overflow-hidden">
                <ProfileBody data={previewData} showAllEvents={showAllEvents} onToggleEvents={() => setShowAllEvents((v) => !v)} embedded />
              </div>
            ) : <div className="p-6 text-center text-muted-foreground">Loading preview…</div>
          )}

          <NavBar
            step={step}
            onPrev={() => setStep((s) => Math.max(0, s - 1))}
            onNext={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            onSave={onSave}
            saving={saving}
            truckId={truckId}
            mode={mode}
          />
        </div>
      )}
      {truckId && draft && (
        <PublishFooter draft={draft} onPublish={onPublish} onUnpublish={onUnpublish} />
      )}
    </div>
  );
}

function Header({ mode = "admin" }) {
  const backTo = mode === "owner" ? "/dashboard" : "/admin";
  const label = mode === "owner" ? "Public profile editor" : "Admin preview · Profile editor";
  return (
    <div className="sticky top-0 z-30 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="max-w-2xl mx-auto px-3 py-2.5 flex items-center gap-2">
        <Link to={backTo} className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground" data-testid="editor-back">
          <ArrowLeft weight="bold" className="h-4 w-4" />
        </Link>
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="mb-4" data-testid="editor-progress">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Profile completion</span>
        <span className="text-xs font-bold text-primary">{pct}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stepper({ step, onStep }) {
  return (
    <div className="flex gap-1 mb-4 overflow-x-auto -mx-4 px-4 no-scrollbar" data-testid="editor-stepper">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onStep(i)}
            className={
              "h-10 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition flex items-center gap-1.5 " +
              (active
                ? "bg-primary text-primary-foreground"
                : done
                ? "bg-primary/10 text-primary border border-primary/40"
                : "bg-card border border-border text-muted-foreground hover:bg-muted")
            }
            data-testid={`editor-step-${s.key}`}
          >
            {done ? <CheckCircle weight="fill" className="h-3.5 w-3.5" /> : <s.icon weight="duotone" className="h-3.5 w-3.5" />}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

function NavBar({ step, onPrev, onNext, onSave, saving, truckId, mode = "admin" }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={onPrev}
        disabled={step === 0}
        className="h-11 px-4 rounded-full border border-border bg-card text-sm font-semibold disabled:opacity-40 hover:bg-muted transition inline-flex items-center gap-1.5"
        data-testid="editor-prev"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="h-11 px-4 rounded-full border border-border bg-card text-sm font-semibold hover:bg-muted transition inline-flex items-center gap-1.5"
        data-testid="editor-save"
      >
        <FloppyDisk weight="duotone" className="h-4 w-4 text-primary" />
        {saving ? "Saving…" : "Save"}
      </button>
      {mode === "admin" && (
        <Link
          to={`/admin/truck-profile-preview/${encodeURIComponent(truckId)}`}
          className="h-11 px-4 rounded-full border border-border bg-card text-sm font-semibold hover:bg-muted transition inline-flex items-center gap-1.5"
          data-testid="editor-preview-link"
        >
          <Eye weight="duotone" className="h-4 w-4 text-primary" /> Open full preview
        </Link>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={step === STEPS.length - 1}
        className="ml-auto h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-40 transition inline-flex items-center gap-1.5"
        data-testid="editor-next"
      >
        Next <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const PUBLISH_MIN_PCT = 60;

function PublishFooter({ draft, onPublish, onUnpublish }) {
  const pct = draft.completion_pct || 0;
  const canPublish = pct >= PUBLISH_MIN_PCT;
  const slug = draft.truck_slug;
  // In the native app window.location.origin is capacitor://localhost —
  // never use it for share/copy links, and never target=_blank there
  // (iOS hands the capacitor:// URL to the OS, opening a random app).
  const isNative = Capacitor.isNativePlatform();
  const publicUrl = slug ? `${PUBLIC_WEB_ORIGIN}/t/${slug}` : "";

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div
      className="sticky bottom-0 left-0 right-0 z-30 border-t border-border bg-card/95 backdrop-blur-md"
      data-testid="publish-footer"
    >
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        {draft.is_published ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
            {isNative ? (
              <Link
                to={`/t/${slug}`}
                className="text-xs font-semibold text-primary hover:underline truncate max-w-[60%]"
                data-testid="publish-footer-public-link"
              >
                /t/{slug}
              </Link>
            ) : (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-primary hover:underline truncate max-w-[60%]"
                data-testid="publish-footer-public-link"
              >
                /t/{slug}
              </a>
            )}
            <button
              type="button"
              onClick={copyLink}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
              aria-label="Copy public link"
              data-testid="publish-footer-copy"
            >
              <LinkSimple weight="bold" className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onUnpublish}
              className="ml-auto h-10 px-4 rounded-full border border-border bg-card text-sm font-semibold hover:bg-muted transition inline-flex items-center gap-1.5"
              data-testid="publish-footer-unpublish"
            >
              <EyeSlash weight="duotone" className="h-4 w-4" />
              Unpublish
            </button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
              <span className="h-2 w-2 rounded-full bg-rose-500" />
              Hidden
            </span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {canPublish
                ? "Customers can't see this yet."
                : `Reach ${PUBLISH_MIN_PCT}% completion to publish (currently ${pct}%).`}
            </span>
            <button
              type="button"
              onClick={onPublish}
              disabled={!canPublish}
              title={canPublish ? "Make this profile visible to customers" : `Reach ${PUBLISH_MIN_PCT}% to enable`}
              className="ml-auto h-10 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed transition inline-flex items-center gap-1.5"
              data-testid="publish-footer-publish"
            >
              <Globe weight="duotone" className="h-4 w-4" />
              Publish
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TruckPicker({ trucks, onSelect, loading, error }) {
  const [q, setQ] = useState("");
  const filtered = trucks.filter((t) => !q || (t.truck_name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-2xl mx-auto px-4 mt-4">
      <h1 className="font-display font-bold text-2xl tracking-tight">Pick a truck to draft a profile for</h1>
      <p className="text-sm text-muted-foreground mt-1">Drafts stay hidden from customers until launch is approved.</p>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search trucks…"
        className="mt-4 w-full h-11 rounded-full bg-card border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="editor-truck-search"
      />
      <div className="mt-4 space-y-2 max-h-[60dvh] overflow-y-auto" data-testid="editor-truck-list">
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="editor-truck-loading">
            Loading trucks…
          </div>
        ) : error ? (
          <div
            className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-300"
            data-testid="editor-truck-error"
          >
            {error}
          </div>
        ) : trucks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground text-center">
            No trucks yet. Invite an owner from the Admin page to get started.
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground text-center">
            No matches for &ldquo;{q}&rdquo;. Try a shorter or different keyword.
          </div>
        ) : (
          filtered.slice(0, 100).map((t) => (
            <button
              key={t.truck_id}
              type="button"
              onClick={() => onSelect(t.truck_id)}
              className="w-full text-left p-3 rounded-2xl border border-border bg-card hover:bg-muted transition flex items-center gap-3"
            >
              <Storefront weight="duotone" className="h-5 w-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{t.truck_name}</div>
                <div className="text-xs text-muted-foreground">
                  {t.has_draft ? `${t.completion_pct}% complete` : "No draft yet"}
                </div>
              </div>
              {t.has_draft && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-1 rounded-full">
                  Draft
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/* ---- Step components ---- */

function StepBasic({ draft, update }) {
  return (
    <div className="space-y-4" data-testid="step-basic">
      <Field label="Truck name">
        <input
          type="text"
          value={draft.truck_name || ""}
          onChange={(e) => update({ truck_name: e.target.value })}
          className="form-input"
          data-testid="field-truck-name"
        />
      </Field>
      <Field label="Short description" helper="One-liner shown under the truck name. ~120 chars works best.">
        <textarea
          value={draft.short_description || ""}
          onChange={(e) => update({ short_description: e.target.value.slice(0, 240) })}
          rows={2}
          className="form-input resize-none"
          data-testid="field-description"
        />
      </Field>
      <Field label="Known for" helper="Optional. Examples: 'Smash burgers', 'Korean tacos'.">
        <input
          type="text"
          value={draft.known_for || ""}
          onChange={(e) => update({ known_for: e.target.value })}
          className="form-input"
          data-testid="field-known-for"
        />
      </Field>
      <Field label="Cuisine tags" helper="Press Enter or comma to add a tag.">
        <TagInput
          value={draft.cuisine_tags || []}
          onChange={(tags) => update({ cuisine_tags: tags })}
        />
      </Field>
      <Field label="Service area" helper="Optional. e.g. 'Lancaster County, PA'">
        <input
          type="text"
          value={draft.service_area || ""}
          onChange={(e) => update({ service_area: e.target.value })}
          className="form-input"
          data-testid="field-service-area"
        />
      </Field>
    </div>
  );
}

function StepPhotos({ draft, update, apiPrefix, isPro = false }) {
  const hasLogo = !!draft.logo_path;
  const adminBlocked = !!draft.admin_disabled_logo_pin;
  // When a Pro owner uploads a logo for the first time, flip the "use
  // logo on map pin" toggle on automatically — almost every Pro owner
  // wants this, and most never noticed the second toggle hidden
  // below. They can still uncheck it if they want the default icon.
  // Free owners skip the auto-flip: they can build out the profile
  // (logo, cover, description) but the custom map pin stays a Pro perk.
  const handleLogoChange = (newPath) => {
    const wasEmpty = !draft.logo_path;
    const becamePresent = !!newPath;
    const patch = { logo_path: newPath };
    if (isPro && wasEmpty && becamePresent && !draft.use_logo_pin && !adminBlocked) {
      patch.use_logo_pin = true;
    }
    update(patch);
  };
  return (
    <div className="space-y-4" data-testid="step-photos">
      <ImageUpload
        label="Logo"
        helper="Square works best. JPG/PNG/WEBP, max 8MB."
        path={draft.logo_path}
        onChange={handleLogoChange}
        testId="upload-logo"
        apiPrefix={apiPrefix}
      />

      {/* Logo Map Pin toggle — Pro-only perk. We render the row always
          so Free owners discover the feature and see the upgrade nudge.
          The checkbox is disabled (with a hint) until the owner uploads
          a logo AND is on a Pro plan. */}
      <div
        className={
          "rounded-xl border p-3 flex items-start gap-3 transition " +
          (adminBlocked
            ? "border-rose-300 bg-rose-50 dark:bg-rose-950/40"
            : !isPro
            ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30"
            : "border-border bg-background/50")
        }
        data-testid="use-logo-pin-row"
      >
        <input
          type="checkbox"
          id="use-logo-pin-toggle"
          className="h-4 w-4 mt-0.5 accent-primary disabled:opacity-50"
          checked={!!draft.use_logo_pin && !adminBlocked && isPro}
          disabled={!hasLogo || adminBlocked || !isPro}
          onChange={(e) => update({ use_logo_pin: e.target.checked })}
          data-testid="use-logo-pin-toggle"
        />
        <label htmlFor="use-logo-pin-toggle" className="min-w-0 flex-1 cursor-pointer">
          <div className="text-sm font-medium leading-tight">
            Use my logo on the map pin
            {!isPro && (
              <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-200 dark:bg-amber-800 dark:text-amber-100 px-1.5 py-0.5 rounded-full" data-testid="use-logo-pin-pro-badge">
                Pro
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            {adminBlocked
              ? "An admin disabled the logo pin for this truck. Reach out to support if you'd like to discuss."
              : !isPro
              ? "Pro perk — upgrade to replace the generic food-category icon with your custom logo on the live map."
              : !hasLogo
              ? "Upload a square logo above first, then come back to turn this on."
              : "Pro perk — your logo replaces the generic food-category icon. Square transparent PNGs look sharpest."}
          </div>
        </label>
        {hasLogo && !adminBlocked && isPro && (
          <div
            className="flex-none h-11 w-11 rounded-full bg-white grid place-items-center shadow-[0_0_0_2px_#ea580c,0_2px_6px_rgba(0,0,0,0.18)] overflow-hidden p-[1px]"
            data-testid="use-logo-pin-preview"
            title="Pin preview"
          >
            <img
              src={`/api/files/${draft.logo_path}`}
              alt=""
              className="h-10 w-10 rounded-full object-cover"
              style={{ objectPosition: `${draft.logo_pin_x ?? 50}% ${draft.logo_pin_y ?? 50}%` }}
            />
          </div>
        )}
      </div>

      {/* Logo focal-point cropper — only meaningful when the owner has
          uploaded a logo AND turned on `use_logo_pin`. Lets owners with
          wide/tall logos (wordmarks, banners) drag the focal point so
          the most important pixels survive the circular `object-fit:
          cover` crop on the map pin. */}
      {hasLogo && draft.use_logo_pin && !adminBlocked && isPro && (
        <LogoFocalCropper
          src={`/api/files/${draft.logo_path}`}
          x={draft.logo_pin_x ?? 50}
          y={draft.logo_pin_y ?? 50}
          onChange={(pos) => update({ logo_pin_x: pos.x, logo_pin_y: pos.y })}
        />
      )}

      <ImageUpload
        label="Cover image"
        helper="Wide banner. Min 800×400."
        path={draft.cover_path}
        onChange={(p) => update({ cover_path: p })}
        testId="upload-cover"
        apiPrefix={apiPrefix}
      />
      {draft.cover_path && (
        <Field label="Cover focal point" helper="Slide to choose which part of the cover stays visible.">
          <div className="space-y-2">
            <div
              className="h-24 w-full rounded-xl overflow-hidden border border-border bg-cover"
              style={{
                backgroundImage: `url(/api/files/${draft.cover_path})`,
                backgroundPosition: `center ${draft.cover_focal_y ?? 50}%`,
              }}
              data-testid="cover-focal-preview"
            />
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={draft.cover_focal_y ?? 50}
              onChange={(e) => update({ cover_focal_y: Number(e.target.value) })}
              className="w-full accent-primary"
              data-testid="field-cover-focal-y"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Top</span><span>Center</span><span>Bottom</span>
            </div>
          </div>
        </Field>
      )}
      <Field label="Photo gallery" helper="Add a few action shots — food, truck, lines, smiles.">
        <GalleryUpload
          paths={draft.gallery_paths || []}
          onChange={(paths) => update({ gallery_paths: paths })}
          apiPrefix={apiPrefix}
        />
      </Field>
    </div>
  );
}

function StepLinks({ draft, update, apiPrefix }) {
  return (
    <div className="space-y-3" data-testid="step-links">
      <Field label="Menu URL" helper="A direct link to your menu (e.g. on your website).">
        <input value={draft.menu_url || ""} onChange={(e) => update({ menu_url: e.target.value })} className="form-input" data-testid="field-menu-url" />
      </Field>
      <Field label="Menu file (image or PDF)">
        <ImageUpload path={draft.menu_file_path} onChange={(p) => update({ menu_file_path: p })} testId="upload-menu-file" apiPrefix={apiPrefix} />
      </Field>
      <Field label="Website"><input value={draft.website || ""} onChange={(e) => update({ website: e.target.value })} className="form-input" placeholder="https://" /></Field>
      <Field label="Facebook"><input value={draft.facebook || ""} onChange={(e) => update({ facebook: e.target.value })} className="form-input" placeholder="https://facebook.com/yourpage" /></Field>
      <Field label="Instagram"><input value={draft.instagram || ""} onChange={(e) => update({ instagram: e.target.value })} className="form-input" placeholder="https://instagram.com/your" /></Field>
      <Field label="TikTok"><input value={draft.tiktok || ""} onChange={(e) => update({ tiktok: e.target.value })} className="form-input" placeholder="https://tiktok.com/@your" /></Field>
      <Field label="Contact email"><input value={draft.contact_email || ""} onChange={(e) => update({ contact_email: e.target.value })} className="form-input" type="email" /></Field>
      <Field label="Contact phone (optional)"><input value={draft.contact_phone || ""} onChange={(e) => update({ contact_phone: e.target.value })} className="form-input" type="tel" /></Field>
    </div>
  );
}

function StepOrdering({ draft, updateOrdering }) {
  const o = draft.ordering || {};
  const urlValid = !o.external_url || /^https?:\/\//i.test(o.external_url);
  return (
    <div className="space-y-4" data-testid="step-ordering">
      <Field label="Online ordering">
        <Toggle
          value={!!o.enabled}
          onChange={(enabled) => updateOrdering({ enabled })}
          testId="ordering-enabled"
        />
      </Field>
      {o.enabled && (
        <>
          <Field label="Type">
            <div className="flex gap-2">
              <RadioPill
                active={o.type === "external"}
                onClick={() => updateOrdering({ type: "external" })}
                testId="ordering-type-external"
              >External link</RadioPill>
              <RadioPill
                active={o.type === "internal"}
                onClick={() => updateOrdering({ type: "internal" })}
                disabled
                testId="ordering-type-internal"
              >TruckTrackr ordering · coming soon</RadioPill>
            </div>
          </Field>
          {o.type === "external" && (
            <Field label="Ordering URL" helper="Where customers complete the order.">
              <input
                value={o.external_url || ""}
                onChange={(e) => updateOrdering({ external_url: e.target.value })}
                className={"form-input " + (!urlValid ? "ring-2 ring-rose-500/40" : "")}
                placeholder="https://order.example.com/yourtruck"
                data-testid="ordering-url"
              />
              {!urlValid && <p className="mt-1 text-xs text-rose-600">Must start with http:// or https://</p>}
            </Field>
          )}
          <Field label="Button label">
            <div className="flex flex-wrap gap-2">
              {ORDERING_LABELS.map((l) => (
                <RadioPill key={l} active={o.button_label === l} onClick={() => updateOrdering({ button_label: l })}>
                  {l}
                </RadioPill>
              ))}
            </div>
          </Field>
          <Field label="Show button">
            <select
              value={o.availability || "always"}
              onChange={(e) => updateOrdering({ availability: e.target.value })}
              className="form-input"
              data-testid="ordering-availability"
            >
              {ORDERING_AVAILABILITY.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Notes (optional)" helper='e.g. "Preorders close 15 minutes before service ends."'>
            <textarea
              value={o.notes || ""}
              onChange={(e) => updateOrdering({ notes: e.target.value })}
              rows={2}
              className="form-input resize-none"
              data-testid="ordering-notes"
            />
          </Field>
        </>
      )}
    </div>
  );
}

/* ---- Atoms ---- */

function Field({ label, helper, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</span>
      {children}
      {helper && <p className="text-[11px] text-muted-foreground mt-1">{helper}</p>}
    </label>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={testId}
      aria-pressed={value}
      className={
        "h-7 w-12 rounded-full transition relative " +
        (value ? "bg-primary" : "bg-muted")
      }
    >
      <span
        className={
          "absolute top-0.5 h-6 w-6 rounded-full bg-card shadow-sm transition-all " +
          (value ? "left-[22px]" : "left-0.5")
        }
      />
    </button>
  );
}

function RadioPill({ active, onClick, children, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={
        "h-9 px-3.5 rounded-full text-xs font-semibold border transition disabled:opacity-50 disabled:cursor-not-allowed " +
        (active
          ? "bg-primary text-primary-foreground border-transparent"
          : "bg-card border-border hover:bg-muted")
      }
    >
      {children}
    </button>
  );
}

function TagInput({ value, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setInput("");
  };
  return (
    <div className="form-input flex flex-wrap gap-1.5 min-h-[44px] items-center" data-testid="field-tags">
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary rounded-full px-2 py-0.5">
          {t}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="hover:opacity-70">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
          if (e.key === "Backspace" && !input && value.length) { onChange(value.slice(0, -1)); }
        }}
        onBlur={add}
        placeholder="Type a tag…"
        className="flex-1 min-w-[100px] bg-transparent text-sm focus:outline-none"
      />
    </div>
  );
}

function ImageUpload({ label, helper, path, onChange, testId, apiPrefix = "/admin" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = path ? `/api/files/${path}` : null;

  const onPick = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB)");
      return;
    }
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files supported here");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${apiPrefix}/profile-drafts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const inner = (
    <div className="flex items-center gap-3">
      <div className="h-16 w-16 rounded-xl bg-muted overflow-hidden flex-shrink-0 grid place-items-center">
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <Camera weight="duotone" className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 flex gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-9 px-3 rounded-full bg-card border border-border text-xs font-semibold hover:bg-muted inline-flex items-center gap-1.5"
          data-testid={testId}
        >
          <UploadSimple className="h-3.5 w-3.5" />
          {busy ? "Uploading…" : url ? "Replace" : "Upload"}
        </button>
        {url && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
            aria-label="Remove"
          >
            <Trash className="h-4 w-4" />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
      />
    </div>
  );

  if (!label) return inner;
  return <Field label={label} helper={helper}>{inner}</Field>;
}

function GalleryUpload({ paths, onChange, apiPrefix = "/admin" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onPick = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const next = [...paths];
    let skipped = 0;
    for (const f of Array.from(files)) {
      if (f.size > 8 * 1024 * 1024) { skipped += 1; continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`${apiPrefix}/profile-drafts/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data.path);
      } catch (e) {
        console.warn("profile editor: image upload failed, file skipped", e);
      }
    }
    onChange(next);
    if (skipped) toast.error(`Skipped ${skipped} file(s) over 8MB`);
    setBusy(false);
  };
  return (
    <div data-testid="gallery-upload">
      <div className="flex flex-wrap gap-2">
        {paths.map((p) => (
          <div key={p} className="relative h-20 w-28 rounded-xl overflow-hidden bg-muted">
            <img src={`/api/files/${p}`} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(paths.filter((x) => x !== p))}
              className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-card/90 hover:bg-card"
              aria-label="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-20 w-28 rounded-xl border-2 border-dashed border-border hover:bg-muted text-xs font-semibold text-muted-foreground inline-flex flex-col items-center justify-center gap-1"
        >
          <UploadSimple className="h-4 w-4" />
          {busy ? "Uploading…" : "Add photo"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
        />
      </div>
    </div>
  );
}
